import {
  EmptyInput,
  Flex,
  Icon,
  InfoItemContainer,
  InputGroup,
  InputGroupBreak,
  InputLabel,
  InputRow,
  ModelDateDMY,
  ModelSelect,
  PageRefName,
  RadioYesNoInput,
  SVGInfo,
  TextFieldInput,
  useRefs,
} from '@equifinance/shared';
import { useMemo } from 'react';
import styled from 'styled-components';
import { ContractType, EmploymentHistory } from '../../../../../contexts/account/IncomeAndEmployment';
import { InfoListItemAction } from '../../../../Errors/PageErrors';
import { NonJobRelatedIncomeRefName } from './NonJobIncomeSection';
import calculateOffsetTop from '../../../../../utils/calculateOffsetTop';
import { useTabNavHeight } from '../../../../AccountTabNav';

const Info = styled.div`${({ theme }) => `
  border-radius: ${theme.borderRadius[2]};
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.1);

  ${InfoItemContainer} {
    margin: 0;
  }
`}`;

const InfoContainer = styled(Flex)`${({ theme }) => `
  justify-content: space-between;
  min-height: ${theme.padding[6]};
  align-items: stretch;
  line-height ${theme.padding[6]};
`}`;

const ReadOnlyInputStyle = styled.div<{ disabled?: boolean }>`${({ theme, disabled }) => `
  flex: 1 1 0%;
  ${disabled && `{
    ${InputLabel} {
      color: ${theme.colours.neutral[6]};
    }
    ${TextFieldInput} {
      background-color: transparent;
      color: ${theme.colours.neutral[6]};
      border: 1px solid ${theme.colours.neutral[6]};
    }
  }`};
`}`;

export default function PositionSectionWorkingStatus({
  employmentHistory,
  field,
}: {
  employmentHistory: EmploymentHistory,
  field: string,
}) {
  const { refs } = useRefs();
  const stickyHeaderOffset = useTabNavHeight();
  const endDateDisabled = useMemo(() => {
    switch (employmentHistory.employmentType) {
      case 'NotWorkingNotRetired':
      case 'NotWorkingRetired':
        return employmentHistory.currentlyNotWorking;
      case 'Employed':
      case 'ConstructionIndustryScheme':
        return employmentHistory.currentlyWorkingInThisRole
          && employmentHistory.contractType !== 'FixedTerm';
      case 'DirectorOfLimitedCompany':
      case 'FosterCarer':
      case 'LimitedLiabilityPartnership':
      case 'Partnership':
      case 'SoleTrader':
        return employmentHistory.currentlyWorkingInThisRole;
      default:
        return false;
    }
  }, [employmentHistory]);
  if (!employmentHistory?.employmentType || employmentHistory?.employmentType === 'None') {
    return null;
  }

  const isEmployedOrCIS = employmentHistory?.employmentType === 'Employed'
    || employmentHistory?.employmentType === 'ConstructionIndustryScheme';

  const scrollToNonJobIncom = () => {
    const { current } = refs?.[NonJobRelatedIncomeRefName] ?? {};
    const offsetTop = calculateOffsetTop(current);

    if (current) {
      refs[PageRefName]?.current.scrollTo({ top: (offsetTop - stickyHeaderOffset), behavior: 'smooth' });
    }
  };

  return (
    <>
      <InputGroupBreak />
      <InputGroup>
        <InputRow>
          {(['NotWorkingRetired', 'NotWorkingNotRetired'].includes(employmentHistory?.employmentType)
            ? (
              <RadioYesNoInput
                label="Currently not working"
                field={`${field}.currentlyNotWorking`}
                required
              />
            )
            : (
              <RadioYesNoInput
                label="Currently working in this role"
                field={`${field}.currentlyWorkingInThisRole`}
                required
              />
            )
          )}
          <EmptyInput />
        </InputRow>
        {isEmployedOrCIS
          && employmentHistory.currentlyWorkingInThisRole && (
            <InputRow>
              <ModelSelect
                label="Contract type"
                placeholder="Please select"
                field={`${field}.contractType`}
                options={ContractType}
                required
              />
              <RadioYesNoInput
                label="Zero hours contract"
                field={`${field}.isZeroHoursContract`}
                required
              />
            </InputRow>
        )}
        <InputRow>
          <ModelDateDMY
            label="Start date"
            field={`${field}.startDate`}
            showDay={false}
            required
          />
          <ReadOnlyInputStyle disabled={endDateDisabled}>
            <ModelDateDMY
              label="End date"
              field={`${field}.endDate`}
              showDay={false}
              required
              clearOnDisabled
              disabled={endDateDisabled}
            />
          </ReadOnlyInputStyle>
        </InputRow>
      </InputGroup>
      {['NotWorkingRetired', 'NotWorkingNotRetired'].includes(employmentHistory?.employmentType || '') && (
        <Info>
          <SVGInfo>
            <InfoContainer>
              Please enter any income in the Non-job related income section below.
              <InfoListItemAction onClick={scrollToNonJobIncom}>
                <Icon className="mi-circle-arrow-right" />
                Go to section
              </InfoListItemAction>
            </InfoContainer>
          </SVGInfo>
        </Info>
      )}
    </>
  );
}
